import { get } from 'lodash';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import {
  FormattedCategory,
  FormattedDTE,
} from '@upstart/harmony/lib/legos/dtEntitiesTypes';

/** Determines if the given dealer has their DMS setup and enabled
 * @param  {Dealer} dealer
 * @returns {boolean} True if enabled, false if not
 */
export const isDMSFlowEnabled = (dealer: Dealer): boolean => {
  if (!dealer.dmsType) return false;

  switch (dealer.dmsType) {
    case DMSType.CDK:
      return Boolean(dealer?.dmsCDK?.dealerId);
    case DMSType.DT:
      return isDealerTrackDMSConfigured(dealer);
    case DMSType.TEKION:
      return isTekionDMSConfigured(dealer);
    default:
      // for any dms type not specified  in the above cases, default to false
      return false;
  }
};

/**
 * Returns true if the Dealer is properly configured for DealerTrack as their DMS system, false otherwise.
 * @param  {Dealer} dealer
 * @returns boolean
 */
export function isDealerTrackDMSConfigured(
  dealer: Pick<Dealer, 'dmsType' | 'dmsConfig'>,
): boolean {
  return Boolean(
    dealer.dmsType === DMSType.DT &&
      dealer?.dmsConfig?.dealertrack?.companyNumber &&
      dealer?.dmsConfig?.dealertrack?.enterpriseCode,
  );
}

/**
 * Returns true if the Dealer is properly configured for Tekion as their DMS system, false otherwise.
 * @param  {Dealer} dealer
 * @returns boolean
 */
export function isTekionDMSConfigured(dealer: Dealer): boolean {
  return Boolean(
    dealer.dmsType === DMSType.TEKION && dealer?.dmsConfig?.tekion?.dealerId,
  );
}

export const isSentToDMS = (deal: Deal) => {
  const dmsDealInfo = get(deal, 'financeMeta.dmsDealInfo');

  return (
    dmsDealInfo &&
    dmsDealInfo.selectedFinanceResponseId &&
    dmsDealInfo.selectedFinanceResponseId === deal.selectedFinanceResponseId
  );
};

export function getDteCostAsFloat(dte?: FormattedDTE): number {
  // safe guarantee that dte is not undefined since it can be invoked from JS
  if (!dte?.cost) return 0;

  return Number(parseFloat(dte.cost.replace(/[^0-9.-]+/g, '')).toFixed(2));
}

export function getUarAddOnEntities(
  dtEntities?: FormattedDTE[],
): FormattedDTE[] {
  // safe guarantee that dtEntities is not undefined since it can be invoked from JS
  if (!dtEntities) return [];

  return dtEntities.filter(
    dte => dte.category === FormattedCategory.AFTERMARKET_OPTION,
  );
}
